import { FunctionComponent } from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";

import { CockpitCard, StatusChip } from "../design";

const toneMap: Record<FlightVerdict["tone"], "neutral" | "info" | "success" | "warning" | "error"> = {
  neutral: "neutral",
  info: "info",
  success: "success",
  warning: "warning",
  error: "error",
};

export const FlightVerdictChips: FunctionComponent<{ verdicts: FlightVerdict[] }> = ({ verdicts }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap aria-label="Flight verdicts">
    {(verdicts || []).map((verdict) => <StatusChip key={verdict.id} label={verdict.label} tone={toneMap[verdict.tone]} />)}
  </Stack>
);

export const FlightFuelCheckSummaryCard: FunctionComponent<{
  summary?: FlightFuelCheckSummary | null;
  unit?: string | null;
  onSelect: (id: string) => void;
}> = ({ summary, unit, onSelect }) => (
  <CockpitCard component="section" aria-labelledby="fuel-checks-summary-title">
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography id="fuel-checks-summary-title" variant="h6">Fuel checks</Typography>
        <StatusChip
          label={!summary?.total ? "No recorded checks" : summary.failed ? `${summary.failed} below minimum` : `${summary.passed} of ${summary.total} above minimum`}
          tone={!summary?.total ? "neutral" : summary.failed ? "error" : "success"}
        />
      </Stack>
      {(summary?.checks || []).map((check) => {
        const base = Math.max(1, Math.abs(check.minimum || 0), Math.abs(check.actual || 0));
        const percent = Math.max(0, Math.min(100, ((check.actual || 0) / base) * 100));
        return (
          <Box key={`${check.waypointId}`} component="button" onClick={() => onSelect(`${check.waypointId}`)} sx={{ p: 1.25, border: "1px solid var(--line)", borderRadius: 1.5, bgcolor: "var(--panel2)", color: "var(--text)", textAlign: "left", cursor: "pointer" }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
              <Typography fontWeight={700}>{check.waypointName || "Waypoint"}</Typography>
              <Stack direction="row" spacing={1.5} sx={{ fontFamily: "var(--font-mono)" }}>
                <Typography variant="caption" color={check.marginVsMin !== null && check.marginVsMin < 0 ? "error" : "success.main"}>vs min {check.marginVsMin === null ? "—" : `${check.marginVsMin > 0 ? "+" : ""}${check.marginVsMin} ${unit || ""}`}</Typography>
                <Typography variant="caption" sx={{ color: "var(--muted)" }}>vs est {check.marginVsEstimate === null ? "—" : `${check.marginVsEstimate > 0 ? "+" : ""}${check.marginVsEstimate} ${unit || ""}`}</Typography>
              </Stack>
            </Stack>
            <LinearProgress variant="determinate" value={percent} color={check.status === "failed" ? "error" : "success"} sx={{ mt: 1, height: 5, borderRadius: 3 }} />
          </Box>
        );
      })}
    </Stack>
  </CockpitCard>
);

const auditDotColor = (event: FlightAuditEvent) => {
  if (event.status === "hold") {
    return "var(--amber)";
  }
  if (event.status === "pending") {
    return "var(--line2)";
  }
  if (event.type === "modified") {
    return "var(--blue)";
  }
  return "var(--green)";
};

const auditTimeLabel = (event: FlightAuditEvent) => {
  if (event.timestamp) {
    return new Date(event.timestamp).toLocaleString("en-GB", { timeZone: "UTC" });
  }
  if (event.status === "hold") {
    return "On hold";
  }
  if (event.status === "pending") {
    return "Pending";
  }
  return "Time unknown";
};

export const FlightAuditTrail: FunctionComponent<{ events: FlightAuditEvent[] }> = ({ events }) => (
  <CockpitCard component="section" aria-labelledby="flight-audit-title" sx={{ p: 1 }}>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
      <Typography id="flight-audit-title" variant="subtitle2" component="h2" sx={{ fontWeight: 700, flexShrink: 0 }}>
        Audit trail
      </Typography>
      {events?.length ? (
        <Box
          component="ol"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: `repeat(${Math.min(events.length, 4)}, minmax(0, 1fr))` },
            gap: 1,
            listStyle: "none",
            p: 0,
            m: 0,
            flex: 1,
            minWidth: 0,
          }}
        >
          {events.map((event) => {
            const caption = [auditTimeLabel(event), event.actor].filter(Boolean).join(" · ");
            return (
              <Box
                component="li"
                key={event.id}
                sx={{
                  position: "relative",
                  borderTop: "2px solid",
                  borderColor: auditDotColor(event),
                  pt: 0.75,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -4,
                    left: 0,
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: auditDotColor(event),
                  }}
                />
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {event.label}
                </Typography>
                <Typography variant="caption" sx={{ color: "var(--muted)", fontFamily: "var(--font-mono)", display: "block" }}>
                  {caption}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: "var(--muted)" }}>
          No audit events recorded for this flight.
        </Typography>
      )}
    </Stack>
  </CockpitCard>
);
